<script lang="ts">
  /**
   * YourSetsSection — the sets story on the Sets tab (Scott, 2026-09-11: "you
   * can't see all of the sets you swam ... on the sets we want to show what
   * sets you swam").
   *
   *   1. "Your sets": every set you've swum as a short card (First / Fastest /
   *      Last, then "See your last N ›"), most-swum first. Scott, 08:04: "Cut
   *      the strip": the "Sets you swam" chip strip is gone; the cards are the
   *      list. How to read them is one caption behind the info tap.
   *   2. "All sets you've swum ›", the ledger's door.
   *   3. The coverage footnote.
   *
   * Coverage honesty: the rows come from the swimFacts cache. While the
   * background scan is still backfilling, the section says "Counted from N of M
   * swims so far." — never an all-time claim.
   */
  import type { SetHistoryRow } from "../lib/setHistory";
  import type { FactsCoverage } from "../lib/allTimeAggregates";
  import { openOverlay } from "../lib/overlay";
  import { haptic } from "../lib/haptics";
  import YourSetsCard from "./YourSetsCard.svelte";

  interface Props {
    rows: SetHistoryRow[];
    coverage: FactsCoverage;
  }
  let { rows, coverage }: Props = $props();

  /** Most-swum first (Scott, 08:04), whether or not the set has a club name; ties keep the most recent first. */
  let cards = $derived([...rows].sort((a, b) => b.timesSwum - a.timesSwum || b.last.dateMs - a.last.dateMs));
  let infoOpen = $state(false);

  function openLedger() {
    haptic("selection");
    openOverlay({ kind: "sets-discovered" });
  }
</script>

<section class="your-sets" aria-label="Your sets">
  {#if rows.length === 0}
    <div class="empty-card">
      <p class="empty-copy">
        Swim a named set (The Rockalita, Killer BS…) and it shows up here with your times.
      </p>
    </div>
  {:else}
    <div class="sec-head">
      <h2 class="sec-label">Your sets</h2>
      <button
        type="button"
        class="info"
        aria-expanded={infoOpen}
        aria-label="How to read your sets"
        onclick={() => (infoOpen = !infoOpen)}
      >i</button>
    </div>
    {#if infoOpen}
      <p class="caption">
        Most-swum first. The coloured strip under each name is that set the last time you swam it, in
        order. Tap a set for your latest swims of it.
      </p>
    {/if}

    {#each cards as row (row.setId)}
      <YourSetsCard {row} />
    {/each}

    <!-- The ledger's door (orch-8 ruling, 2026-09-11): one small link, no copy above it. -->
    <button type="button" class="all-sets" data-all-sets onclick={openLedger}>
      All sets you've swum <span aria-hidden="true">›</span>
    </button>
  {/if}

  {#if !coverage.complete}
    <p class="coverage-note">Counted from {coverage.counted} of {coverage.total} swims so far.</p>
  {/if}
</section>

<style>
  .your-sets {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .sec-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sec-label {
    font: var(--type-title-card);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: 0 2px;
  }
  /* A 44 px target around a small circled i. */
  .info {
    position: relative;
    width: 44px;
    height: 44px;
    margin: -10px -10px -10px 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--text-muted);
    font-family: var(--font-text);
    font-size: 13px;
    font-weight: 700;
    font-style: italic;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .info::before {
    content: "";
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1.5px solid var(--text-muted);
  }
  .info[aria-expanded="true"] {
    color: var(--text-primary);
  }
  .info:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: -6px;
  }
  .caption {
    margin: 0 2px;
    font-size: 14px;
    line-height: 1.4;
    color: var(--text-muted);
  }

  /* Same link style as a card's "See your last N ›". */
  .all-sets {
    align-self: flex-start;
    min-height: 44px;
    padding: 0 4px;
    margin-left: -2px;
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font-family: var(--font-text);
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .all-sets:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }

  .empty-card {
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--space-4);
  }
  .empty-copy {
    margin: 0;
    font-size: 15px;
    line-height: 1.45;
    color: var(--text-muted);
  }

  .coverage-note {
    font-size: 14px;
    color: var(--text-muted);
    margin: calc(-1 * var(--space-1)) 2px 0;
    line-height: 1.35;
  }
</style>
